import React, { useEffect, useState } from "react";
import {
  Container,
  Spinner,
  Col,
  Dropdown,
  Button,
  Row,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

import {
  fetchFinancialData,
  onChangeTime,
} from "../store/slices/financialSlice";
import { fetchStatus } from "../store/slices/preferenceSlice";
import { fetchHsFields, fetchTxFields } from "../store/slices/mappingSlice";

import {
  setShowErrorModal,
  setShowSuccessModal,
} from "../store/slices/preferenceSlice";
import { getUrl } from "../helpers/getUrl";

import SetupFieldsDropdown from "../components/SetupFieldsDropdown";

import arrowIcon from "../assets/images/arrowIcon.svg";

function FinancialIframe() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [btnLoading, setBtnLoading] = useState(false);
  // financial state
  const {
    timeDropdownFields,
    timePreference,
    financialObject,
    lastSynced,
    financialData: { loading },
  } = useSelector((state) => state.financial);

  const {
    txFilterPreference,
    hubspotFilterPreference,
    hubspotCustomerNumberPreference,
    customerNumberPreference,
  } = useSelector((state) => state.contact);

  const {
    hubspot: {
      hubspotDropdownFields: { customerSearchFields: hsFilterFields },
      loading: hsLoading,
    },
    tripletex: {
      tripletexDropdownFields: { customerSearchFields: txFilterFields },
      loading: txLoading,
    },
  } = useSelector((state) => state.mapping);

  /**
   * This function is used for showing test in dropdown
   */
  const toggleDropdownText = () => {
    if (!timePreference) {
      return "Select";
    } else {
      return timeDropdownFields.find((field) => field.value === timePreference)
        .name;
    }
  };

  /**
   * This function is used for updating the financial data
   */
  const onUpdateFinancialData = async () => {
    try {
      setBtnLoading(true);
      const portalId = searchParams.get("portalId");
      const objectId = searchParams.get("objectId");

      if (!timePreference) throw new Error("Please select the time preference");

      // Save customer preferences
      await axios.post(getUrl("REACT_APP_SAVE_PREFERENCES"), {
        portalId,
        preferences: {
          filterOptions: {
            filterHsProperty: hubspotFilterPreference,
            filterTxProperty: txFilterPreference,
          },
          hubspotCustomerNumber: {
            direction: customerNumberPreference.value,
            mapping: {
              hubspotProperty: hubspotCustomerNumberPreference,
            },
          },
        },
        preferenceType: "customer",
      });

      await axios.post(getUrl("REACT_APP_UPDATE_FINANCIAL_DATA"), {
        portalId,
        hs_object_id: objectId,
        time: timePreference,
      });

      dispatch(
        fetchFinancialData({ portalId, objectId: `company_${objectId}` })
      );

      dispatch(
        setShowSuccessModal({ message: "Financial data updated successfully" })
      );
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(
        setShowErrorModal({
          message: errorMessage,
        })
      );
      throw error;
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    const portalId = searchParams.get("portalId");
    const objectId = searchParams.get("objectId");

    if (portalId && objectId) {
      dispatch(
        fetchFinancialData({ portalId, objectId: `company_${objectId}` })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const portalId = searchParams.get("portalId");
    if (portalId) {
      dispatch(fetchStatus(portalId));
      dispatch(fetchHsFields(portalId));
      dispatch(fetchTxFields(portalId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Container className="d-flex flex-row justify-content-center">
        <Col xs={12} lg={8} className="px-0">
          {loading || hsLoading || txLoading ? (
            <div
              className="d-flex flex-row justify-content-center align-items-center"
              style={{ height: "50vh" }}
            >
              <Spinner
                animation="border"
                variant="primary"
                role="status"
              ></Spinner>
            </div>
          ) : (
            <div className="financial-window py-4 px-3 my-5">
              <div className="text-center">
                <h5>Financial Report</h5>
                {lastSynced && <p>(Last Updated on {lastSynced})</p>}
              </div>

              <div className="mt-5 search-params">
                <p style={{ fontSize: "16px" }} className="mb-2">
                  <b>Customer Search property</b>
                </p>
                <p>
                  This section consists of the search parameters based on which
                  the search or sync will be performed.
                </p>

                {hsFilterFields &&
                  hsFilterFields.length > 0 &&
                  txFilterFields &&
                  txFilterFields.length > 0 && (
                    <Row className="mt-3 align-items-center">
                      <Col sm={5} lg={3}>
                        <b>HubSpot</b>
                        <SetupFieldsDropdown
                          fieldsData={hsFilterFields}
                          dropdownFor="hubspotFilterFields"
                          selectedPreference={hubspotFilterPreference}
                        />
                      </Col>
                      <Col
                        sm={1}
                        lg={1}
                        className="mx-0 sync-settings-card-direction"
                      >
                        <div className="d-flex flex-row justify-content-center">
                          <img
                            src={arrowIcon}
                            alt="arrow-icon"
                            className="mt-3"
                          />
                        </div>
                      </Col>
                      <Col sm={5} lg={3}>
                        <b>Tripletex</b>
                        <SetupFieldsDropdown
                          fieldsData={txFilterFields}
                          dropdownFor="txFilterFields"
                          selectedPreference={txFilterPreference}
                        />
                      </Col>
                    </Row>
                  )}
              </div>

              <div className="d-flex flex-row align-items-center mt-4">
                <p className="mb-0 me-3">
                  <b>Time:</b>
                </p>
                <Col sm={5} lg={4}>
                  <Dropdown>
                    <Dropdown.Toggle className="setup-fields-dropdown w-100">
                      {toggleDropdownText()}
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                      renderOnMount={true}
                      popperConfig={{ strategy: "fixed" }}
                    >
                      <div className="setup-fields-dropdown-items-container">
                        {timeDropdownFields &&
                          timeDropdownFields.map((field) => (
                            <Dropdown.Item
                              key={field.value}
                              onClick={() =>
                                dispatch(
                                  onChangeTime({ selectedOption: field.value })
                                )
                              }
                            >
                              {field.name}
                            </Dropdown.Item>
                          ))}
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </div>

              <ul className="mt-5 financial-unordered-list ps-3">
                {financialObject &&
                  Object.keys(financialObject).map((key) => (
                    <li key={key}>
                      <p className="d-flex flex-row justify-content-between">
                        <span>{key}</span>
                        <span>{financialObject[key]}</span>
                      </p>
                    </li>
                  ))}
              </ul>

              <div className="d-flex flex-row justify-content-end mt-5">
                <Button
                  className="save-changes-button"
                  disabled={btnLoading}
                  onClick={() => onUpdateFinancialData()}
                >
                  {btnLoading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    "Update"
                  )}
                </Button>
              </div>
            </div>
          )}
        </Col>
      </Container>
    </div>
  );
}

export default FinancialIframe;
